import { useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { PrezzoConvenzionato } from "@/components/accordi/PrezzoConvenzionato";
import { CoopSaluteChat } from "@/components/CoopSaluteChat";
import { toast } from "sonner";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";

const RUOLI = [["aderente", "Aderente (chi sottoscrive)"], ["coniuge", "Coniuge / unito civilmente"], ["convivente", "Convivente di fatto"], ["figlio", "Figlio/a"], ["altro", "Altro"]];

const Persona = ({ p, i, onChange, onRemove }) => (
  <div className="flex items-center gap-2" data-testid={`cs-persona-${i}`}>
    <select value={p.ruolo} onChange={(e) => onChange({ ...p, ruolo: e.target.value })} disabled={i === 0} data-testid={`cs-persona-${i}-ruolo`} className="h-10 flex-1 rounded-md border border-input bg-white px-3 text-sm">
      {RUOLI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
    <Input type="number" min={0} max={110} value={p.eta} onChange={(e) => onChange({ ...p, eta: e.target.value })} placeholder="Età" className="w-24" data-testid={`cs-persona-${i}-eta`} />
    {i > 0 && <Button variant="ghost" size="icon" onClick={onRemove} data-testid={`cs-persona-${i}-rimuovi`} aria-label="Rimuovi"><Trash2 className="h-4 w-4" /></Button>}
  </div>
);

// Cooperazione Salute 4You: motore ufficiale con lo sconto dell'accordo + Assistente 4You per le domande.
export const CalcCoopSalute = ({ codice, onSalva }) => {
  const [persone, setPersone] = useState([{ ruolo: "aderente", eta: "" }]);
  const [nucleo, setNucleo] = useState(true);
  const [res, setRes] = useState(null);
  const [scelta, setScelta] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = { persone: persone.map((p) => ({ ruolo: p.ruolo, eta: p.eta === "" ? null : Number(p.eta) })), nucleo };
  const cambia = (lista) => { setPersone(lista); setRes(null); };

  const calcola = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/accordi/calcola", { codice, product: "cooperazione_salute", input });
      if (!data.ok) throw new Error(data.errore);
      setRes(data); setScelta((data.soluzioni.find((s) => s.consigliata) || data.soluzioni[0]).code);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-5" data-testid="calc-cs">
      <div className="min-w-0 space-y-4 xl:col-span-3">
        <div className="space-y-2">
          <div className="text-sm font-medium text-forest">Chi vuoi proteggere? Indica ruolo ed età, senza nomi.</div>
          {persone.map((p, i) => <Persona key={i} p={p} i={i} onChange={(np) => cambia(persone.map((x, j) => (j === i ? np : x)))} onRemove={() => cambia(persone.filter((_, j) => j !== i))} />)}
          {persone.length < 10 && <Button variant="outline" size="sm" onClick={() => cambia([...persone, { ruolo: "figlio", eta: "" }])} className="gap-1" data-testid="cs-aggiungi-persona"><Plus className="h-4 w-4" />Aggiungi una persona</Button>}
          {persone.length > 1 && <label className="flex items-center gap-2 text-sm text-forest"><Checkbox checked={nucleo} onCheckedChange={(v) => { setNucleo(!!v); setRes(null); }} data-testid="cs-nucleo" />Formano un unico nucleo familiare</label>}
        </div>
        <Button onClick={calcola} disabled={busy || persone.some((p) => p.eta === "")} data-testid="cs-calcola" className="bg-forest hover:bg-forest-hover">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confronta i piani</Button>
        {res && <>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="cs-soluzioni">
            {res.soluzioni.map((s) => (
              <button key={s.code} onClick={() => setScelta(s.code)} data-testid={`cs-soluzione-${s.code}`}
                className={`space-y-2 rounded-2xl border-2 bg-white p-4 text-left transition-all ${scelta === s.code ? "border-terracotta shadow-md" : "border-border/60 hover:border-forest/40"}`}>
                <div className="flex items-center justify-between gap-2"><span className="font-heading font-semibold text-forest">{s.nome}</span>{scelta === s.code ? <Check className="h-5 w-5 text-terracotta" /> : s.consigliata && <span className="rounded-full bg-sage px-2 py-0.5 text-[11px] text-forest">Consigliata</span>}</div>
                <p className={`text-xs ${s.completa ? "text-muted-foreground" : "text-amber-800"}`}>{s.descrizione}</p>
                <PrezzoConvenzionato r={s} testid={`cs-prezzo-${s.code}`} />
              </button>
            ))}
          </div>
          {res.avvisi?.length > 0 && <ul className="text-xs text-amber-800" data-testid="cs-avvisi">{res.avvisi.map((a) => <li key={a}>• {a}</li>)}</ul>}
          <Button onClick={() => onSalva({ ...input, pacchetto: scelta })} disabled={!scelta} data-testid="cs-salva" className="bg-terracotta hover:bg-terracotta-hover">Salva il piano scelto</Button>
        </>}
      </div>
      <div className="min-w-0 xl:col-span-2">
        <CoopSaluteChat endpoint="/public/accordi/coopsalute/chat" extraBody={{ codice }} historyQuery={`?codice=${encodeURIComponent(codice)}`} storageKey={`cs_session_${codice}`}
          intro="Chiedi all'Assistente 4You cosa coprono i piani, le carenze o le differenze tra Salute e Sostegno. Usa solo ruolo ed età, mai nomi o dati sulla salute. Il contributo con lo sconto dell'accordo è nel calcolatore."
          suggest={["Che differenza c'è tra Salute e Sostegno?", "Quali sono le carenze per un ricovero?", "Posso includere mio figlio di 20 anni?"]} />
      </div>
    </div>
  );
};
